import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  FileText,
  Settings,
  Menu,
  LogOut,
  Store,
  Box,
  ArrowRightLeft,
  ShoppingBag,
  Wrench,
  CreditCard,
  Shield,
  ShieldCheck,
  FileBarChart,
  Layers,
  Tag,
  Cpu,
  Ruler,
  UserPlus,
  Phone,
  UsersRound,
  Truck,
  ClipboardCheck,
  Receipt,
  History,
  Plus,
  PenTool,
  Cog,
  BarChart3,
  PieChart,
  Briefcase,
  FileCheck,
  PackagePlus,
  Clipboard,
  Award,
  Search,
  X,
  ChevronDown,
  User,
  Wallet,
  Activity,
  Repeat,
  Printer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import { all_routes } from "../../Router/all_routes";
import { hiddenNavGroups, hiddenNavItems } from "../../config/profiles";
import { useSelector } from "react-redux";
import { useTheme } from "@/components/theme-provider";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";

const navGroups = [
  {
    label: "Main",
    items: [
      {
        title: "Dashboard",
        href: all_routes.dashboard,
        icon: LayoutDashboard,
        permission: "dashboard view",
      },
      {
        title: "POS",
        href: all_routes.pos,
        icon: ShoppingCart,
        permission: ["cart read", "cart create"],
      },
      {
        title: "Stores",
        href: all_routes.storeList,
        icon: Store,
        permission: "store read",
      },
      { title: "Setup Guide", href: all_routes.onboarding, icon: Award },
    ],
  },
  {
    label: "Sales",
    items: [
      {
        title: "Sales List",
        href: all_routes.saleslist,
        icon: ShoppingBag,
        permission: "sale read",
      },
      {
        title: "Quotations",
        href: all_routes.quotationsList,
        icon: FileText,
        permission: "quotation read",
      },
      {
        title: "Recurring Invoices",
        href: all_routes.recurringInvoices,
        icon: Repeat,
        permission: "recurring invoice read",
      },
      {
        title: "Payments",
        href: "/payments",
        icon: Receipt,
        permission: "payment read",
      },
      {
        title: "Cart History",
        href: "/cart-history",
        icon: History,
        permission: "cart read",
      },
      {
        title: "Warranty Claims",
        href: all_routes.warrantyClaims,
        icon: ShieldCheck,
        permission: "warranty claim read",
      },
    ],
  },
  {
    label: "Repairs",
    items: [
      {
        title: "Repair Dashboard",
        href: all_routes.repairDashboard,
        icon: Wrench,
        permission: "repair dashboard view",
      },
      {
        title: "Predefined Repairs",
        href: "/predefined-repairs",
        icon: PenTool,
        permission: "predefined repair job read",
      },
      {
        title: "Repair Categories",
        href: "/repair-categories",
        icon: Layers,
        permission: "repair category read",
      },
      {
        title: "Special Parts",
        href: "/special-parts",
        icon: Cog,
        permission: "special part read",
      },
    ],
  },
  {
    label: "Purchases",
    items: [
      {
        title: "Purchase Orders",
        href: all_routes.purchaselist,
        icon: ShoppingCart,
        permission: "purchase order read",
      },
      {
        title: "GRNs",
        href: "/grn",
        icon: Clipboard,
        permission: "goods received note read",
      },
    ],
  },
  {
    label: "Contacts",
    items: [
      {
        title: "Customers",
        href: all_routes.customers,
        icon: Users,
        permission: "customer read",
      },
      {
        title: "Suppliers",
        href: all_routes.suppliers,
        icon: Truck,
        permission: "supplier read",
      },
      {
        title: "Customer Groups",
        href: "/customer-groups",
        icon: UsersRound,
        permission: "customer group read",
      },
    ],
  },
  {
    label: "Inventory",
    items: [
      {
        title: "Stock Adjustments",
        href: "/stock-adjustments",
        icon: Box,
        permission: "stock adjustment read",
      },
      {
        title: "Stock Transfers",
        href: all_routes.stocktransfer,
        icon: ArrowRightLeft,
        permission: "stock transfer read",
      },
      {
        title: "Opening Stock",
        href: all_routes.openingStock,
        icon: PackagePlus,
        permission: [
          "inventory serial bulk_create",
          "inventory serial create",
          "inventory create",
        ],
      },
      {
        title: "Serial Tracking",
        href: all_routes.serialtrack,
        icon: History,
        permission: "inventory serial track",
      },
    ],
  },
  {
    label: "Products",
    items: [
      {
        title: "All Products",
        href: all_routes.productlist,
        icon: Package,
        permission: "product read",
      },
      {
        title: "Categories",
        href: "/categories",
        icon: Layers,
        permission: "category product read",
      },
      { title: "Brands", href: "/brands", icon: Tag, permission: "brand read" },
      {
        title: "Models",
        href: "/brand-models",
        icon: Cpu,
        permission: "brand model read",
      },
      { title: "Units", href: "/units", icon: Ruler, permission: "unit read" },
    ],
  },
  {
    label: "Computer Assembly",
    items: [
      {
        title: "Assemble Configs",
        href: all_routes.assembleConfigs,
        icon: Cpu,
        permission: "assemble config read",
      },
      {
        title: "Assemble Jobs",
        href: all_routes.assembleJobs,
        icon: Wrench,
        permission: "assemble job read",
      },
    ],
  },
  {
    label: "Employees",
    items: [
      {
        title: "Employee List",
        href: "/employees",
        icon: Users,
        permission: "employee read",
      },
      {
        title: "Designations",
        href: "/designations",
        icon: Award,
        permission: "employee designations read",
      },
      {
        title: "Tasks",
        href: "/tasks",
        icon: FileText,
        permission: "task read",
      },
    ],
  },
  {
    label: "Accounts",
    items: [
      {
        title: "Expenses",
        href: all_routes.expenses,
        icon: CreditCard,
        permission: "expense read",
      },
      {
        title: "Expense Categories",
        href: "/expense-categories",
        icon: Layers,
        permission: "expense category read",
      },
    ],
  },
  {
    label: "Reports",
    items: [
      {
        title: "Stock Report",
        href: "/stock-reports",
        icon: BarChart3,
        permission: "report product stock view",
      },
      {
        title: "Expense Report",
        href: "/expense-reports",
        icon: FileText,
        permission: "report expense view",
      },
      {
        title: "Sales Summary",
        href: "/sales-summary-report",
        icon: PieChart,
        permission: "report sales summary view",
      },
      {
        title: "Customerwise Sales",
        href: "/customerwise-sales-report",
        icon: FileText,
        permission: "report customerwise sales view",
      },
      {
        title: "Sales Invoice",
        href: "/sales-invoice-report",
        icon: FileText,
        permission: "report sales invoice list view",
      },
      {
        title: "Supplierwise Expenses",
        href: "/supplierwise-expense-report",
        icon: FileText,
        permission: "report supplierwise expense view",
      },
      {
        title: "Parts Usage",
        href: "/parts-usage-report",
        icon: FileBarChart,
        permission: "report parts usage view",
      },
      {
        title: "Daily Cash Balance",
        href: "/daily-cash-balance-report",
        icon: Wallet,
        permission: "report daily cash balance view",
      },
      {
        title: "Inventory Movement",
        href: "/inventory-movement-report",
        icon: Activity,
        permission: "report inventory movement view",
      },
      {
        title: "Profit & Loss",
        href: "/profit-loss-report",
        icon: FileText,
        permission: "report profit loss view",
      },
      {
        title: "Stock Transfer Report",
        href: all_routes.stockTransferReport,
        icon: ArrowRightLeft,
        permission: "report stock transfer list view",
      },
    ],
  },
  {
    label: "Settings",
    items: [
      {
        title: "Printers",
        href: all_routes.printers,
        icon: Printer,
        permission: "printer read",
      },
      {
        title: "Payment Methods",
        href: all_routes.paymentMethods,
        icon: CreditCard,
        permission: "payment-method read",
      },
      {
        title: "Templates",
        href: all_routes.templates,
        icon: FileText,
        permission: "template read",
      },
    ],
  },
  {
    label: "Administration",
    items: [
      {
        title: "Users",
        href: all_routes.administration,
        icon: Shield,
        permission: "user read",
      },
      {
        title: "Roles",
        href: all_routes.roles,
        icon: ShieldCheck,
        permission: "role read",
      },
    ],
  },
];

// Filter groups and items based on the active build profile
const visibleNavGroups = navGroups
  .filter((group) => !hiddenNavGroups.has(group.label))
  .map((group) => ({
    ...group,
    items: group.items.filter((item) => !hiddenNavItems.has(item.title)),
  }))
  .filter((group) => group.items.length > 0);

const allNavItems = visibleNavGroups.flatMap((group) =>
  group.items.map((item) => ({ ...item, groupLabel: group.label })),
);

export function Sidebar({ className, onLogout }) {
  const { theme } = useTheme();
  const location = useLocation();
  const token = useSelector((state) => state.bearer_token);
  const userData = useSelector((state) => state.user_data) || {};
  const currentStore = useSelector((state) => state.current_store);
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const [searchQuery, setSearchQuery] = useState("");

  const hasPermission = (permission) => {
    if (!permission) return true;
    if (Array.isArray(permission)) {
      return permission.some((p) => permissions.includes(p));
    }
    return permissions.includes(permission);
  };

  const getInitialExpandedGroups = () => {
    const expanded = new Set(["Main"]);
    for (const group of visibleNavGroups) {
      if (group.items.some((item) => item.href === location.pathname)) {
        expanded.add(group.label);
        break;
      }
    }
    return expanded;
  };

  const [expandedGroups, setExpandedGroups] = useState(
    getInitialExpandedGroups,
  );

  const toggleGroup = (label) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const { data: onboardingStatus } = useQuery({
    queryKey: ["onboarding-status"],
    queryFn: async () => {
      const res = await apiClient.get("/init-page/onboarding");
      return res.data.data;
    },
    enabled: !!token,
    staleTime: 60000,
  });

  const isOnboardingCompleted =
    onboardingStatus?.is_email_changed &&
    onboardingStatus?.is_password_updated &&
    onboardingStatus?.is_store_setup_completed &&
    onboardingStatus?.have_products &&
    onboardingStatus?.have_customers;

  const fullName = userData.employee?.full_name || userData.username || "User";

  return (
    <div
      className={cn(
        "border-r border-border min-h-screen flex flex-col opacity-100",
        theme === "dark" ? "bg-slate-950" : "bg-white",
        className,
      )}
    >
      <div className="px-6 py-4 h-24 flex items-center">
        <Link to={all_routes.dashboard} className="flex items-center gap-3 group">
          <div className="h-11 w-11 rounded-xl overflow-hidden bg-white dark:bg-card flex items-center justify-center border border-border shadow-xs group-hover:scale-105 transition-transform shrink-0 p-1">
            <img
              src="/assets/img/logo-small.png"
              alt="Easy Retail Flow"
              className="h-full w-full object-contain"
            />
          </div>
          <div className="flex flex-col overflow-hidden">
            <span className="text-base font-bold leading-tight tracking-tight text-foreground truncate group-hover:text-primary transition-colors">
              Easy Retail Flow
            </span>
            <span className="text-[10px] font-semibold text-primary uppercase tracking-wider mt-0.5 whitespace-nowrap">
              Multi-Store POS
            </span>
          </div>
        </Link>
      </div>

      <div className="px-4 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search menu..."
            className={searchQuery ? "pl-9 pr-9 w-full" : "pl-9 w-full"}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 px-3 overflow-y-auto custom-sidebar-scroll">
        <div className="space-y-6 pb-6 pt-2">
          {searchQuery.trim() ? (
            // Flat search results
            <div className="px-3">
              {(() => {
                const results = allNavItems.filter((item) => {
                  if (item.permission && !hasPermission(item.permission)) {
                    return false;
                  }
                  if (
                    item.requirePrintAgent &&
                    !(currentStore?.print_agent_enabled === true &&
                      currentStore?.print_agent_pairing_key != null &&
                      currentStore?.print_agent_pairing_key !== "")
                  ) {
                    return false;
                  }
                  return (
                    item.title
                      .toLowerCase()
                      .includes(searchQuery.toLowerCase()) &&
                    !(
                      item.href === all_routes.onboarding &&
                      isOnboardingCompleted
                    )
                  );
                });
                if (results.length === 0) {
                  return (
                    <p className="text-xs text-muted-foreground text-center py-6">
                      No results for &ldquo;{searchQuery}&rdquo;
                    </p>
                  );
                }
                return (
                  <div className="space-y-1">
                    {results.map((item) => (
                      <Button
                        key={item.href}
                        variant={
                          location.pathname === item.href
                            ? "secondary"
                            : "ghost"
                        }
                        className={cn(
                          "w-full justify-start h-9 text-sm font-medium transition-all duration-200",
                          location.pathname === item.href
                            ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/10"
                            : "text-muted-foreground hover:text-foreground hover:bg-accent",
                        )}
                        asChild
                        onClick={() => setSearchQuery("")}
                      >
                        <Link to={item.href}>
                          <item.icon className="mr-2 h-4 w-4 shrink-0" />
                          <span className="flex-1 truncate">{item.title}</span>
                          <span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70 shrink-0">
                            {item.groupLabel}
                          </span>
                        </Link>
                      </Button>
                    ))}
                  </div>
                );
              })()}
            </div>
          ) : (
            // Normal grouped nav
            visibleNavGroups.map((group) => {
              const filteredItems = group.items.filter((item) => {
                if (
                  item.href === all_routes.onboarding &&
                  isOnboardingCompleted
                ) {
                  return false;
                }
                if (item.permission && !hasPermission(item.permission)) {
                  return false;
                }
                if (
                  item.requirePrintAgent &&
                  !(currentStore?.print_agent_enabled === true &&
                    currentStore?.print_agent_pairing_key != null &&
                    currentStore?.print_agent_pairing_key !== "")
                ) {
                  return false;
                }
                return true;
              });

              if (filteredItems.length === 0) return null;

              const isExpanded = expandedGroups.has(group.label);

              return (
                <div key={group.label} className="px-3">
                  <button
                    onClick={() => toggleGroup(group.label)}
                    className="w-full flex items-center justify-between mb-1 px-1 py-1 rounded hover:bg-accent/50 transition-colors group/header"
                  >
                    <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground group-hover/header:text-muted-foreground transition-colors">
                      {group.label}
                    </h3>
                    <ChevronDown
                      className={cn(
                        "h-3 w-3 text-muted-foreground/70 transition-transform duration-200",
                        isExpanded ? "rotate-0" : "-rotate-90",
                      )}
                    />
                  </button>
                  <div
                    className={cn(
                      "space-y-1 overflow-hidden transition-all duration-200",
                      isExpanded
                        ? "max-h-[500px] opacity-100"
                        : "max-h-0 opacity-0",
                    )}
                  >
                    {filteredItems.map((item) => (
                      <Button
                        key={item.href}
                        variant={
                          location.pathname === item.href
                            ? "secondary"
                            : "ghost"
                        }
                        className={cn(
                          "w-full justify-start h-9 text-sm font-medium transition-all duration-200",
                          location.pathname === item.href
                            ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/10"
                            : "text-muted-foreground hover:text-foreground hover:bg-accent",
                        )}
                        asChild
                      >
                        <Link to={item.href}>
                          <item.icon className="mr-2 h-4 w-4" />
                          {item.title}
                        </Link>
                      </Button>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="px-4 mt-auto border-t pt-4 pb-2">
        <div className="p-3 bg-muted/50 rounded-xl mb-3 text-[hsl(var(--foreground))]">
          <div className="flex items-center gap-3 mb-2">
            <Avatar className="h-10 w-10 border border-border transition-transform hover:scale-105">
              <AvatarImage src={userData.image} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                <User className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
            <div className="overflow-hidden">
              <p className="text-sm font-bold text-foreground truncate">
                {fullName}
              </p>
              <p
                className="text-[10px] text-muted-foreground truncate opacity-80"
                title={userData.employee?.email}
              >
                {userData.employee?.email || "No Email"}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start text-[10px] font-bold h-8 border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20 transition-all active:scale-95 text-muted-foreground bg-transparent group"
            onClick={onLogout}
          >
            <LogOut className="mr-2 h-3.5 w-3.5 group-hover:rotate-12 transition-transform" />
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
}

export function MobileSidebar({ onLogout }) {
  const { theme } = useTheme();
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const token = useSelector((state) => state.bearer_token);
  const userData = useSelector((state) => state.user_data) || {};
  const currentStore = useSelector((state) => state.current_store);
  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    if (!permission) return true;
    if (Array.isArray(permission)) {
      return permission.some((p) => permissions.includes(p));
    }
    return permissions.includes(permission);
  };

  const getInitialExpandedGroups = () => {
    const expanded = new Set(["Main"]);
    for (const group of visibleNavGroups) {
      if (group.items.some((item) => item.href === location.pathname)) {
        expanded.add(group.label);
        break;
      }
    }
    return expanded;
  };

  const [expandedGroups, setExpandedGroups] = useState(
    getInitialExpandedGroups,
  );

  const toggleGroup = (label) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const { data: onboardingStatus } = useQuery({
    queryKey: ["onboarding-status"],
    queryFn: async () => {
      const res = await apiClient.get("/init-page/onboarding");
      return res.data.data;
    },
    enabled: !!token,
    staleTime: 60000,
  });

  const isOnboardingCompleted =
    onboardingStatus?.is_email_changed &&
    onboardingStatus?.is_password_updated &&
    onboardingStatus?.is_store_setup_completed &&
    onboardingStatus?.have_products &&
    onboardingStatus?.have_customers;

  const fullName = userData.employee?.full_name || userData.username || "User";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          className="mr-2 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 xl:hidden"
        >
          <Menu className="h-6 w-6" />
          <span className="sr-only">Toggle Menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className={cn(
          "p-0 text-foreground border-r border-border flex flex-col shadow-2xl opacity-100 sm:max-w-[400px] md:max-w-[440px]",
          theme === "dark" ? "bg-slate-950" : "bg-white",
        )}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Navigation Menu</SheetTitle>
          <SheetDescription>
            Access navigation links and user settings.
          </SheetDescription>
        </SheetHeader>
        <div className="px-7 py-6 h-20 flex items-center border-b">
          <Link
            to={all_routes.dashboard}
            className="flex items-center gap-3 group"
            onClick={() => setOpen(false)}
          >
            <div className="h-10 w-10 rounded-xl overflow-hidden bg-white dark:bg-card flex items-center justify-center border border-border shadow-xs group-hover:scale-105 transition-transform shrink-0 p-1">
              <img
                src="/assets/img/logo-small.png"
                alt="Easy Retail Flow"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="flex flex-col text-foreground overflow-hidden">
              <span className="text-sm font-bold leading-tight tracking-tight truncate group-hover:text-primary transition-colors">
                Easy Retail Flow
              </span>
              <span className="text-[10px] font-semibold text-primary uppercase tracking-wider mt-0.5 whitespace-nowrap">
                Multi-Store POS
              </span>
            </div>
          </Link>
        </div>
        <div className="px-4 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search menu..."
              className={searchQuery ? "pl-9 pr-9 w-full" : "pl-9 w-full"}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <ScrollArea className="flex-1 px-3">
          <div className="space-y-6 py-4">
            {searchQuery.trim() ? (
              // Flat search results
              <div className="px-3">
                {(() => {
                  const results = allNavItems.filter((item) => {
                    if (item.permission && !hasPermission(item.permission)) {
                      return false;
                    }
                    if (
                      item.requirePrintAgent &&
                      !(currentStore?.print_agent_enabled === true &&
                        currentStore?.print_agent_pairing_key != null &&
                        currentStore?.print_agent_pairing_key !== "")
                    ) {
                      return false;
                    }
                    return (
                      item.title
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase()) &&
                      !(
                        item.href === all_routes.onboarding &&
                        isOnboardingCompleted
                      )
                    );
                  });
                  if (results.length === 0) {
                    return (
                      <p className="text-xs text-muted-foreground text-center py-6">
                        No results for &ldquo;{searchQuery}&rdquo;
                      </p>
                    );
                  }
                  return (
                    <div className="space-y-1">
                      {results.map((item) => (
                        <Button
                          key={item.href}
                          variant={
                            location.pathname === item.href
                              ? "secondary"
                              : "ghost"
                          }
                          className={cn(
                            "w-full justify-start h-9 text-sm font-medium transition-all duration-200",
                            location.pathname === item.href
                              ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/10"
                              : "text-muted-foreground hover:text-foreground hover:bg-accent",
                          )}
                          asChild
                          onClick={() => {
                            setOpen(false);
                            setSearchQuery("");
                          }}
                        >
                          <Link to={item.href}>
                            <item.icon className="mr-2 h-4 w-4 shrink-0" />
                            <span className="flex-1 truncate">
                              {item.title}
                            </span>
                            <span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70 shrink-0">
                              {item.groupLabel}
                            </span>
                          </Link>
                        </Button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ) : (
              // Normal grouped nav
              visibleNavGroups.map((group) => {
                const filteredItems = group.items.filter((item) => {
                  if (
                    item.href === all_routes.onboarding &&
                    isOnboardingCompleted
                  ) {
                    return false;
                  }
                  if (item.permission && !hasPermission(item.permission)) {
                    return false;
                  }
                  if (
                    item.requirePrintAgent &&
                    !(currentStore?.print_agent_enabled === true &&
                      currentStore?.print_agent_pairing_key != null &&
                      currentStore?.print_agent_pairing_key !== "")
                  ) {
                    return false;
                  }
                  return true;
                });

                if (filteredItems.length === 0) return null;

                const isExpanded = expandedGroups.has(group.label);

                return (
                  <div key={group.label} className="px-3">
                    <button
                      onClick={() => toggleGroup(group.label)}
                      className="w-full flex items-center justify-between mb-1 px-1 py-1 rounded hover:bg-accent/50 transition-colors group/header"
                    >
                      <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground group-hover/header:text-muted-foreground transition-colors">
                        {group.label}
                      </h3>
                      <ChevronDown
                        className={cn(
                          "h-3 w-3 text-muted-foreground/70 transition-transform duration-200",
                          isExpanded ? "rotate-0" : "-rotate-90",
                        )}
                      />
                    </button>
                    <div
                      className={cn(
                        "space-y-1 overflow-hidden transition-all duration-200",
                        isExpanded
                          ? "max-h-[500px] opacity-100"
                          : "max-h-0 opacity-0",
                      )}
                    >
                      {filteredItems.map((item) => (
                        <Button
                          key={item.href}
                          variant={
                            location.pathname === item.href
                              ? "secondary"
                              : "ghost"
                          }
                          className={cn(
                            "w-full justify-start h-9 text-sm font-medium transition-all duration-200",
                            location.pathname === item.href
                              ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/10"
                              : "text-muted-foreground hover:text-foreground hover:bg-accent",
                          )}
                          asChild
                          onClick={() => setOpen(false)}
                        >
                          <Link to={item.href}>
                            <item.icon className="mr-2 h-4 w-4" />
                            {item.title}
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
        <div className="p-3 border-t mt-auto">
          <div className="p-3 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Avatar className="h-9 w-9 border border-border">
                <AvatarImage src={userData.image} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  <User className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <div className="overflow-hidden">
                <p className="text-xs font-semibold text-foreground truncate">
                  {fullName}
                </p>
                <p className="text-[10px] text-muted-foreground truncate italic">
                  {userData.employee?.email || "No Email Address"}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start text-[10px] h-7 border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20 transition-all duration-200 text-muted-foreground bg-transparent group"
              onClick={onLogout}
            >
              <LogOut className="mr-2 h-3 w-3 group-hover:scale-110" />
              Sign Out
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
